import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import './StudentVerifyClassCode.scss';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import InputField from '../../../components/InputField/InputField';
import ButtonStyle from '../../../components/ButtonStyle/ButtonStyle';
import { FC, useEffect, useRef, useState } from 'react';
import { CircularProgress, Tooltip } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { GET_VIRTUAL_SCHOOL_CLASSCODES_LIST, VERIFY_CLASS_CODE_STUDENT } from '../api/AdminLoginQuery';
import ToasterWithMessage from '../../../components/toaster';
import { 
    FETCH_POLICY_NO_CACHE, 
    GET_CLASS_CODE_FOR_VIRTUAL_SCHOOL_TEXT, 
    KATON_VIRTUAL_SCHOOL_TITLE, 
    messages, 
    NO_DATA_FOUND, 
    PATH_ADDRESSES, 
    patterns, 
    PTA_ROLE, 
    STUDENT_ROLE 
} from '../../../utils/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { login, logout, setLoginPage, setSelectedClassRoomId } from '../../../redux/slices/authSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { signIn } from "aws-amplify/auth"
import WarningPopup from '../../../components/WarningPopup/WarningPopup';
import images from '../../../assets/images';
import { handleInputWithoutSpaces } from '../../../utils/helper';
import { MeetingCategoryType } from '../../AdminPta/AdminPta';
import { VERIFY_GENERAL_CATEGORY, VERIFY_MEETING_CATEGORY } from '../../AdminPta/api/PtaQuery';

interface StudentVerifyClassCodeProps {
    loginType: 'student' | 'pta' | 'ptaVerify',
    selectedCategory?: MeetingCategoryType
}

interface IFormInput{
    passcode: string;
}

//validation
const schema = yup.object().shape({
    passcode: yup.string().required('Code is required')
            .uppercase()
            .length(6, 'Code must be exactly 6 characters')
            .matches(patterns?.onlyAlphaNumeric, messages?.validCode)
});


const StudentVerifyClassCode:FC<StudentVerifyClassCodeProps> = ({ loginType, selectedCategory }) => {
 
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {error} = ToasterWithMessage();
  const methods = useForm<IFormInput>({resolver: yupResolver(schema)});
  const { handleSubmit, setFocus, setValue, reset } = methods;
  const userName = process.env.REACT_APP_PTA_USERNAME;
  const password = process.env.REACT_APP_PTA_PASSWORD;
  const [openWarningPopup, setOpenWarningPopup] = useState<boolean>(false);
  const { sorrygif } = images;
  const role = useAppSelector((state) => state.auth.role);
  const location = useLocation();
  const [studentClassCode, setStudentClassCode] = useState<string>("")
  const [showGetClassCodeInfo, setShowGetClassCodeInfo] = useState<boolean>(false);
  const [virtualClassCodes, setVirtualClassCodes] = useState<{ grade: string; code: string }[]>([]);
  const [isOverflow, setIsOverflow] = useState<boolean[]>([]);
  const classNameRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [verifyClassCodeForStudentV2] = useLazyQuery(VERIFY_CLASS_CODE_STUDENT, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
        if(data?.verifyClassCodeForStudentV2?.cId){
            localStorage.setItem("studentClassCode", studentClassCode || "")
            dispatch(setSelectedClassRoomId({selectedClassRoomId: data?.verifyClassCodeForStudentV2?.cId}));
            navigate(PATH_ADDRESSES?.STUDENT_CLASSROOM_PATH);
        }
    },
    onError: (err) => {
        setDisableButton(false);
        error(err?.message);
    }
  });

  const [getVirtualSchoolClassCodesList, {loading}] = useLazyQuery(GET_VIRTUAL_SCHOOL_CLASSCODES_LIST, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
                const apiList = data?.getAClassCodeForVirtualClass || [];
                const mapped = apiList.map((item: any) => ({
                    grade: item?.cOriginalName ?? item?.cName ?? '',
                    code: item?.cCode ?? '',
                }));
                setVirtualClassCodes(mapped);
    },
    onError: (err) => {
        setShowGetClassCodeInfo(false);
        console.log("error", err);
    }
  });


  const [verifyGeneralMeetingCode] = useLazyQuery(VERIFY_GENERAL_CATEGORY, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
        navigate('/pta-meetings');
        setDisableButton(true);
        localStorage.setItem('isGuest','true');
        localStorage.setItem('saId',data?.verifyGeneralMeetingCode)
    },
    onError: (err) => {
        setDisableButton(false);
        dispatch(logout());
        error(err?.message);
    }
  });

  const [verifyMeetingCategory] = useLazyQuery(VERIFY_MEETING_CATEGORY, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
        localStorage.setItem('isVerify','true');
        navigate(role === PTA_ROLE ? '/pta-meetings' : '/teacher-pta', {
            state: {
                category: selectedCategory
            }
        });
        setDisableButton(true);
    },
    onError: (err) => {
        setDisableButton(false);
        setOpenWarningPopup(true);
        setValue('passcode', '');
    }
  });

      //To check the classroomname is overflow
    const checkOverflow = (index: number) => {
        if (classNameRefs.current[index]) {
            const overflow = classNameRefs.current[index]!.scrollWidth > classNameRefs.current[index]!.clientWidth;
            setIsOverflow(prev => {
                const newOverflow = [...prev];
                newOverflow[index] = overflow;
                return newOverflow;
            });
        }
    };

  const onSubmit: SubmitHandler<IFormInput> = async(data) => {
        setDisableButton(true);
        if (loginType === 'student') {
           setStudentClassCode(data.passcode)
            verifyClassCodeForStudentV2({
                variables: {
                    cCode: data?.passcode
                }
            })
        }
        else if (loginType === 'pta' && userName && password) {
            await handleSignIn(userName,password);
            verifyGeneralMeetingCode({
                variables: {
                    mCode: data?.passcode
                }
            }).then(() => {
                localStorage.setItem('generalCode',data?.passcode);
                localStorage.setItem('isVerify','false');
            })
        }
        else if (loginType === 'ptaVerify') {
            verifyMeetingCategory({
                variables: {
                    mCode: data?.passcode,
                    mcId: selectedCategory?.mcId
                }
            })
        }
  }

  const handleSignIn = async(userName: string, password: string) => {
    await signIn({username: userName,password: password})
    .then((datas) => {
        if(datas?.isSignedIn){
            const accessToken : any =localStorage.getItem(Object.keys(localStorage).find((key) => key.includes('accessToken')) as string) || null;
            const token = JSON.parse(window.atob(accessToken.split('.')[1]));
            const role = token['cognito:groups'][0];
            dispatch(setLoginPage({loginPage: 'pta'}));
            dispatch(login({role: role ? role : null}));
        }
    })
    .catch((err) => {console.log("err while auto signin",err)})
}

  useEffect(() => {
    setFocus('passcode');
  }, [setFocus])

  useEffect(() => {
    setValue('passcode', '');
    reset();
  },[selectedCategory])

  const closeWarnPopup = () => {
    setOpenWarningPopup(false);
  }

  useEffect(() => {
    if(showGetClassCodeInfo){
        getVirtualSchoolClassCodesList({
            variables: {}
        });
    }
  }, [showGetClassCodeInfo]);

  useEffect(() => {
    const callCheckOverflow = () => {
        if (virtualClassCodes.length > 0) {
        virtualClassCodes.forEach((_, index) => {
            checkOverflow(index);
        });
        }
    }

    callCheckOverflow();

    window.addEventListener('resize', callCheckOverflow);

    return () => {
        window.removeEventListener('resize', callCheckOverflow);
    };
  }, [virtualClassCodes]);
  
  return (
    <>
    {!showGetClassCodeInfo && <div className={`studentLoginCtn ${location?.pathname?.includes('/student-verifyCode') ? "studentVerifyCode" : ""}`}>
    <FormProvider {...methods}>
    <div className='verifyMainContainer'>
        <form autoComplete="off" className="classCodeForm" onSubmit={handleSubmit(onSubmit)} noValidate style={{gap: (role === STUDENT_ROLE) ? '10px' : ''}}>
            <div className='inputFieldContainer'>
                <div className='labelStyle'>{loginType === 'student' ? 'Join with class code' : 'Join with Passcode'}</div>
                <InputField label="passcode" name="passcode" type="text" size="fullSUpper" onInput={handleInputWithoutSpaces}/>
            </div>
            <div 
                className={`submitButtonContainer ${ loginType === "ptaVerify" && "ptaSubmitButtonContainer"}`} 
                style = {{flexDirection: (role === STUDENT_ROLE) ? 'column' : 'row', alignItems: (role === STUDENT_ROLE) ? 'center' : ''}}
            >
                <ButtonStyle disableButton={disableButton} text="Continue" type="submit" width="154px"/>
                {(role === STUDENT_ROLE) && 
                <div 
                    className='textStyleForGetClassCode' 
                    onClick={() => setShowGetClassCodeInfo((prev) => !prev)}
                >{GET_CLASS_CODE_FOR_VIRTUAL_SCHOOL_TEXT}</div>
                }
            </div>
        </form>
    </div>
    <WarningPopup
        message='Sorry this meeting code is Incorrect'
        openWarningPopup={openWarningPopup}
        closeWarnPopup={closeWarnPopup}
        gifData={sorrygif}
    />
    </FormProvider>
    </div>}
    {showGetClassCodeInfo && <div className='katonVirtualSchoolInfoParentContainer'>
        <div className='katonVirtualSchoolInfoInnerContainer'>
            <div className='virtualSchoolTitleContainer'>
                <div className='virtualSchoolMainTitileContainer'>{KATON_VIRTUAL_SCHOOL_TITLE}</div>
                <div className='virtualSchoolDescContainer'>{`Join the Katon Live Class with the Class codes listed below.`}</div>
            </div>
            <div className='virtualSchoolClassesListContainer'>
                {loading ? (
                  <div className='loadingContainer'>
                    <CircularProgress size={28} />
                  </div>
                ) : (
                  <div className='classesTableWrapper'>
                      <div className='classesTableHeader'>
                          <div className='gradeColumn'>GRADE</div>
                          <div className='classCodeColumn'>Class codes</div>
                      </div>
                      {(virtualClassCodes && virtualClassCodes?.length > 0) ? <div className='classesTableBody'>
                          {virtualClassCodes?.map((classItem, index) => (
                              <div key={index} className='classesTableRow'>
                                <Tooltip 
                                    title={<span>{classItem?.grade}</span>}
                                    disableHoverListener={!isOverflow[index]}
                                    placement="top"
                                >
                                    <div className='gradeColumn' ref={(el) => (classNameRefs.current[index] = el)} onMouseEnter={() => checkOverflow(index)}>
                                        {classItem?.grade}
                                    </div>
                                </Tooltip>
                                  <div className='classCodeColumn'>{classItem?.code}</div>
                              </div>
                          ))}
                      </div>
                      : <div className='nodataTextContainer'>{NO_DATA_FOUND}</div>
                      }
                  </div>
                )}
            </div>
            <div className='backButtonContainer'>
                 <ButtonStyle text="Back" height = "40px" width="240px" onClick={() => setShowGetClassCodeInfo(false)}/>
            </div>
        </div>
    </div>}
    </>
  )
}

export default StudentVerifyClassCode
